import { useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { PackageFilterForm } from 'legacyfrontend/components/filters';
import { useTranslation } from 'react-i18next';

import PackagesApi from 'legacyfrontend/api/paquetes';
import { dataDefaultValues, ROUTE_KEYS, SESSION_STORAGE_KEYS } from 'legacyfrontend/constants';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import useAvailableDates from 'legacyfrontend/hooks/useAvailableDates';

import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import ErrorSnackbar from '../../ErrorSnackbar';

const { PACKAGE_ADVICE } = SESSION_STORAGE_KEYS;
const { HOME, BUSCAR_X_PAQUETES } = ROUTE_KEYS;
const routesToRenderFullPackageSearch = [HOME];
const routesToRenderSmallPackageSearch = [BUSCAR_X_PAQUETES];

const initialPackageSetter = (params) => {
  const originId = params?.get('originId');
  const originName = params?.get('originName');
  const destinationId = params?.get('destinationId');
  const destinationName = params?.get('destinationName');


  const parseOriginId = (idString) => {
    if (!idString) return null;

    if (idString.includes(',')) {
      return idString;
    }

    const parsed = Number(idString);
    return isNaN(parsed) ? null : parsed;
  };

  const parsedOriginId = parseOriginId(originId);
  const parsedDestinationId = parseOriginId(destinationId);

  const result = {
    origin:
      parsedOriginId && originName
        ? { id: parsedOriginId, name: originName.trim() }
        : null,
    destination:
      destinationId && destinationName
        ? { id: parsedDestinationId, name: destinationName.trim() }
        : null,
    adultsQty: params?.get('adultsQty') ? Number(params.get('adultsQty')) : 1,
    selectedMonth: params?.get('monthYear') ? parseInt(params.get('monthYear').split('-')[0]) : null,
    selectedYear: params?.get('monthYear') ? parseInt(params.get('monthYear').split('-')[1]) : null,
  };

  return result;
};

const PackageSearch = forwardRef(
  (
    {
      isOnTargetPath,
      background,
      text,
      onCloseMobileSearch,
      onSearchStart,
      onSearchComplete,
      isMobile,
      isLoading = false,
    },
    ref
  ) => {
    const { t } = useTranslation('layout');
    const history = useHistory();
    const { pathname, search } = useLocation();

    const { registerOnSearch } = useGlobal();

    // Estados para paquetes
    const [packageOrigins, setPackageOrigins] = useState(dataDefaultValues);
    const [packageDestinations, setPackageDestinations] = useState(dataDefaultValues);
    const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now());
    const [isDestinationEmpty, setIsDestinationEmpty] = useState(false);
    const [isOriginEmpty, setIsOriginEmpty] = useState(false);
    const [isDatesEmpty, setIsDatesEmpty] = useState(false);
    const [isTuristasEmpty, setTuristasEmpty] = useState(false);

    // Estados de error
    const [errorMessage, setErrorMessage] = useState('');
    const [openToast, setOpenToast] = useState(false);

    // Estados para mobile
    const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);
    const [hasAutoSearched, setHasAutoSearched] = useState(false);

    // Formularios
    const initialPackageFormInput = useMemo(
      () =>
        initialPackageSetter(
          !!search && pathname === ROUTE_KEYS.BUSCAR_X_PAQUETES
            ? new URLSearchParams(search)
            : undefined
        ),
      [search]
    );

    const [packageFormInput, setPackageFormInput] = useState(initialPackageFormInput);

    useEffect(() => {
      if (!packageFormInput) return;

      if (packageFormInput.origin) {
        setIsOriginEmpty(false);
      }
      if (packageFormInput.destination) {
        setIsDestinationEmpty(false);
      }
      if (packageFormInput.selectedMonth && packageFormInput.selectedYear) {
        setIsDatesEmpty(false);
      }
      if (packageFormInput.adultsQty >= 1) {
        setTuristasEmpty(false);
      }
    }, [
      packageFormInput?.origin,
      packageFormInput?.destination,
      packageFormInput?.selectedMonth,
      packageFormInput?.selectedYear,
      packageFormInput?.adultsQty,
    ]);

    useEffect(() => {
      if (initialPackageFormInput) {
        setPackageFormInput(initialPackageFormInput);
      }
    }, [initialPackageFormInput]);

    // Hooks
    const originId = packageFormInput.origin?.id;
    const destinationId = packageFormInput.destination?.id;
    const { availableDates, isLoading: isLoadingDates, clearAvailableDates } = useAvailableDates(
      originId,
      destinationId
    );

    useImperativeHandle(ref, () => ({
      openFirstDialog: () => {
      },
      getFormInput: () => packageFormInput,
      triggerSearch: () => {
        handleSearchButton({ preventDefault: () => { } });
      },
      isFormValid: () => {
        return !!(
          packageFormInput.origin &&
          packageFormInput.destination &&
          packageFormInput.selectedMonth &&
          packageFormInput.selectedYear &&
          packageFormInput.adultsQty >= 1 &&
          availableDates &&
          Object.keys(availableDates).length > 0
        );
      },
    }));

    const handlePackageOrigin = (origin) => {
      setPackageFormInput((state) => ({
        ...state,
        origin,
        destination: null,
      }));

      // Solo limpiar las fechas si realmente cambió el origen
      if (packageFormInput.origin?.id !== origin?.id) {
        clearAvailableDates();
      }

      if (isMobile) {
        setIsMobileSearchActive(true);
      }
    };

    const handlePackageDestination = (destination) => {
      setPackageFormInput((state) => ({ ...state, destination }));

      if (isMobile) {
        setIsMobileSearchActive(true);
      }
    };

    const handleOccupationChange = (adultsQty) => {
      setPackageFormInput((state) => ({ ...state, adultsQty }));
    };

    const handleDateRangeSelect = (
      startDate,
      endDate,
      selectedMonth = null,
      selectedYear = null
    ) => {
      setPackageFormInput((prevState) => {
        const newState = {
          ...prevState,
          dateSince: startDate,
          dateUntil: endDate,
          selectedMonth,
          selectedYear,
        };
        return newState;
      });
    };

    const handlePackageMonthYear = useCallback((selectedMonth, selectedYear) => {
      setPackageFormInput((prevState) => ({
        ...prevState,
        selectedMonth,
        selectedYear,
      }));
    }, []);

    const showError = useCallback((message) => {
      setErrorMessage(message);
      setOpenToast(true);
    }, []);

    const handleCloseToast = useCallback(() => {
      setOpenToast(false);
    }, []);

    const fetchOrigins = useCallback(async () => {
      try {
        const originsResponse = await PackagesApi.getOrigins();
        window.sessionStorage.removeItem(PACKAGE_ADVICE);
        setPackageOrigins({ isLoading: false, error: false, data: originsResponse });
      } catch (error) {
        showError(error?.message);
        if (!window.sessionStorage.getItem(PACKAGE_ADVICE)) {
          window.sessionStorage.setItem(PACKAGE_ADVICE, true);
        }
        setPackageOrigins({ data: [], error: true, isLoading: false });
      }
    }, []);

    const fetchDestinations = useCallback(async () => {
      try {
        const originIdRaw = packageFormInput?.origin?.id;
        const originId = originIdRaw 
          ? (Array.isArray(originIdRaw) ? originIdRaw.join(',') : String(originIdRaw))
          : new URLSearchParams(search).get('destinationId');
        const destinationsResponse = await PackagesApi.getDestinations(originId);
        setPackageDestinations({
          isLoading: false,
          error: false,
          data: destinationsResponse,
        });
      } catch (error) {
        showError(error?.message);
        setPackageDestinations({ data: [], error: true, isLoading: false });
      }
    }, [packageFormInput, search]);

    const getPackageQueryString = useCallback(() => {
      const { origin, destination, adultsQty, selectedMonth, selectedYear } = packageFormInput;

      const params = new URLSearchParams();

      if (selectedMonth && selectedYear) {
        const monthYear = `${selectedMonth.toString().padStart(2, '0')}-${selectedYear}`;
        params.set('monthYear', monthYear);
      } else {
        return '';
      }

      if (origin && origin.id !== null && origin.id !== undefined) {
        const originIds = Array.isArray(origin.id) ? origin.id.join(',') : String(origin.id);
        params.set('originId', originIds);
        params.set('originName', origin.name);
      }

      if (destination && destination.id !== null && destination.id !== undefined) {
        const destinationIds = Array.isArray(destination.id)
          ? destination.id.join(',')
          : String(destination.id);
        params.set('destinationId', destinationIds);
        params.set('destinationName', destination.name);
      }

      if (adultsQty) params.set('adultsQty', adultsQty);

      params.set('cache', getCacheStatus());
      params.set('_refresh', refreshTimestamp);

      return `?${params.toString()}`;
    }, [packageFormInput, refreshTimestamp]);

    const linkTo = useMemo(
      () => ({
        pathname: ROUTE_KEYS.BUSCAR_X_PAQUETES,
        search: getPackageQueryString(),
      }),
      [getPackageQueryString]
    );

    const handleMobileSearchComplete = () => {
      setIsMobileSearchActive(false);
      if (onCloseMobileSearch) {
        onCloseMobileSearch();
      }
      if (onSearchComplete) {
        onSearchComplete();
      }
    };

    const handleSearchButton = useCallback(
      (e) => {
        e.preventDefault();

        if (!packageFormInput.origin) {
          setIsOriginEmpty(true);
          showError('Por favor seleccione un origen');
          return;
        }

        if (!packageFormInput.destination) {
          setIsDestinationEmpty(true);
          showError('Por favor seleccione un destino');
          return;
        }

        if (!search || search.length <= 1) {
          if (!availableDates || Object.keys(availableDates).length === 0) {
            setIsDatesEmpty(true);
            showError('No hay fechas disponibles para este destino');
            return;
          }
        }

        if (!packageFormInput.selectedMonth || !packageFormInput.selectedYear) {
          setIsDatesEmpty(true);
          showError('Por favor seleccione las fechas');
          return;
        }
        if (!packageFormInput.adultsQty || packageFormInput.adultsQty < 1) {
          setTuristasEmpty(true);
          showError('Por favor seleccione la cantidad de pasajeros');
          return;
        }

        try {
          if (onSearchStart) {
            onSearchStart();
          }

          setRefreshTimestamp(Date.now());

          const searchUrl = linkTo.pathname + getPackageQueryString();
          history.push(searchUrl);
          registerOnSearch();

          if (isMobile && handleMobileSearchComplete) {
            handleMobileSearchComplete();
          }
        } catch (error) {
          showError(error?.message);
          if (onSearchComplete) {
            onSearchComplete();
          }
        }
      },
      [
        linkTo,
        packageFormInput,
        getPackageQueryString,
        history,
        registerOnSearch,
        showError,
        availableDates,
        isMobile,
        handleMobileSearchComplete,
      ]
    );

    // Effects
    useEffect(() => {
      const shouldFetchOrigins = [
        ...routesToRenderFullPackageSearch,
        ...routesToRenderSmallPackageSearch,
      ].includes(pathname);

      if (shouldFetchOrigins) {
        setPackageOrigins((state) => ({ ...state, isLoading: true }));
      }
    }, [pathname]);

    useEffect(() => {
      if (packageOrigins?.isLoading) {
        fetchOrigins();
      } else if (
        routesToRenderSmallPackageSearch.includes(pathname) &&
        packageFormInput?.origin?.id &&
        packageOrigins?.data?.length
      ) {
        const originId = packageFormInput.origin.id;
        let origin;

        if (typeof originId === 'string' && originId.includes(',')) {
          const ids = originId.split(',').map(id => Number(id.trim()));
          origin = packageOrigins.data.find(({ id }) => ids.includes(id));
        } else {
          origin = packageOrigins.data.find(({ id }) =>
            Array.isArray(originId)
              ? originId.includes(id)
              : originId === id
          );
        }

        if (!origin) {
          history.push(ROUTE_KEYS.HOME);
        }
      }
    }, [packageOrigins, pathname, packageFormInput.origin?.id, history, fetchOrigins]);

    useEffect(() => {
      const originId = packageFormInput.origin?.id;
      if (originId) {
        setPackageDestinations((state) => ({ ...state, isLoading: true }));
      } else {
        setPackageDestinations(dataDefaultValues);
      }
    }, [packageFormInput.origin?.id]);

    useEffect(() => {
      if (packageDestinations?.isLoading) {
        fetchDestinations();
      } else if (
        routesToRenderSmallPackageSearch.includes(pathname) &&
        packageFormInput?.destination?.id &&
        packageDestinations?.data?.length
      ) {
        const destinationId = packageFormInput.destination.id;
        let destination = null;

        if (Array.isArray(destinationId)) {
          destination = packageDestinations.data.find(({ id }) => 
            destinationId.includes(id)
          );
        } else if (typeof destinationId === 'string' && destinationId.includes(',')) {
          const ids = destinationId.split(',').map(id => Number(id.trim()));
          destination = packageDestinations.data.find(({ id }) => 
            ids.includes(id)
          );
        } else {
          destination = packageDestinations.data.find(({ id }) => 
            Number(destinationId) === id
          );
        }
      }
    }, [
      packageDestinations,
      pathname,
      packageFormInput.destination?.id,
      history,
      fetchDestinations,
    ]);

    useEffect(() => {
      if (ROUTE_KEYS.HOME === pathname) {
        sessionStorage.clear();
      }
    }, [pathname]);

    useEffect(() => {
      if (hasAutoSearched || isMobile) return;
      if (!packageFormInput) return;

      const hasAllRequiredValues =
        packageFormInput.origin &&
        packageFormInput.destination &&
        packageFormInput.selectedMonth &&
        packageFormInput.selectedYear &&
        packageFormInput.adultsQty >= 1;

      const hasUrlParams = search && search.length > 1;

      if (hasAllRequiredValues && hasUrlParams && pathname === BUSCAR_X_PAQUETES) {
        setHasAutoSearched(true);

        try {
          handleSearchButton({ preventDefault: () => { } });
        } catch (error) {
          console.error('Error en búsqueda automática:', error);
        }
      }
    }, [packageFormInput, search, pathname, hasAutoSearched, availableDates, isMobile]);

    const isSearchButtonDisabled = () => {
      return (
        !packageFormInput.origin ||
        !packageFormInput.destination ||
        !availableDates ||
        Object.keys(availableDates).length === 0 ||
        isLoadingDates
      );
    };

    if (isMobile) {
      return (
        <>
          <div className='package-search__form__container'>
            <div className='package-search__form__fields'>
              <PackageFilterForm
                origins={packageOrigins}
                destinations={packageDestinations}
                handleOriginSelect={handlePackageOrigin}
                handleDestinationSelect={handlePackageDestination}
                handlePackageMonthYear={handlePackageMonthYear}
                handleOccupationChange={handleOccupationChange}
                formInput={packageFormInput}
                isDestinationEmpty={isDestinationEmpty}
                setIsDestinationEmpty={setIsDestinationEmpty}
                isOriginEmpty={isOriginEmpty}
                setIsOriginEmpty={setIsOriginEmpty}
                isDatesEmpty={isDatesEmpty}
                isTuristasEmpty={isTuristasEmpty}
                setIsDatesEmpty={setIsDatesEmpty}
                departureDate={packageFormInput.dateSince}
                onMobileSearchComplete={handleMobileSearchComplete}
                isMobileSearchActive={false}
                isLoading={isLoading}
              />
            </div>
          </div>

          <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
        </>
      );
    }

    if (!isOnTargetPath) return <></>;

    return (
      <>
        <div className='package-search__form__container'>
          <div
            className='package-search__form__fields'
            style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
          >
            <PackageFilterForm
              origins={packageOrigins}
              destinations={packageDestinations}
              handleOriginSelect={handlePackageOrigin}
              handleDestinationSelect={handlePackageDestination}
              handlePackageMonthYear={handlePackageMonthYear}
              handleOccupationChange={handleOccupationChange}
              formInput={packageFormInput}
              isDestinationEmpty={isDestinationEmpty}
              setIsDestinationEmpty={setIsDestinationEmpty}
              isOriginEmpty={isOriginEmpty}
              setIsOriginEmpty={setIsOriginEmpty}
              isDatesEmpty={isDatesEmpty}
              isTuristasEmpty={isTuristasEmpty}
              setIsDatesEmpty={setIsDatesEmpty}
              departureDate={packageFormInput.dateSince}
              onMobileSearchComplete={handleMobileSearchComplete}
              isMobileSearchActive={isMobileSearchActive}
            />
            <div className='package-search__button-row'>
              <button
                onClick={handleSearchButton}
                disabled={isSearchButtonDisabled()}
                className='package-search__button big-package-search__button'
                style={{
                  backgroundColor: background,
                  color: text,
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                }}
              >
                <i className='fa fa-search heading__icon' />
                {t('common.search') || 'Buscar'}
              </button>
            </div>
          </div>
        </div>

        <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
      </>
    );
  }
);

export default PackageSearch;
